/*
 * Team's Leads view (PROD-9977): a manager or super user picks an active L1-L3 rep and sees
 * exactly that rep's My Leads page. The pick is kept in the URL as ?rep=.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Btn } from '../ui';
import { get_roster as getRoster } from '../claim_api';
import { api_error_reason as apiErrorReason } from '../../sales_team/api';
import LeadsSelect from './LeadsSelect';
import MyLeadsPage from './MyLeadsPage';
import { PickerBar } from './MyLeadsView';
import TeamShiftSummary from './TeamShiftSummary';
import {
  groupRepOptions,
  optionsForLevel,
  repFromSearch,
  searchWithRep,
  teamRosterOptions
} from './my_leads';
import type { RepOption } from './my_leads';
import type { RepLevel } from '../../sales_team/api';

const PICK_ID = 'team-leads-rep';

function TeamLeadsPage() {
  const location = useLocation();
  const history = useHistory();
  const [options, setOptions] = useState<RepOption[] | null>(null);
  const [rosterError, setRosterError] = useState('');
  const [level, setLevel] = useState<RepLevel | ''>('');
  const requested = repFromSearch(location.search);

  const loadRoster = useCallback(() => {
    setRosterError('');
    getRoster()
      .then((members) => setOptions(teamRosterOptions(members)))
      .catch((e: unknown) => setRosterError(apiErrorReason(e, 'Could not load the team')));
  }, []);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const pick = (opsUuid: string) => {
    const search = searchWithRep(location.search, opsUuid);
    if (search !== location.search) history.replace({ pathname: location.pathname, search });
  };

  const shown = useMemo(
    () => (options ? optionsForLevel(options, level, requested) : null),
    [options, level, requested]
  );
  const groups = useMemo(() => (shown ? groupRepOptions(shown) : undefined), [shown]);
  const selected = options?.find((o) => o.value === requested) || null;
  const unknownRep = !!requested && !!options && !selected;

  const select = (
    <LeadsSelect
      id={PICK_ID}
      label="Team member"
      value={selected ? selected.value : ''}
      options={shown || []}
      groups={groups}
      placeholder={options ? 'Choose a team member' : 'Loading team…'}
      disabled={!options}
      onChange={pick}
      width={260}
      active={!selected}
    />
  );

  // Picked: the dropdown moves into that rep's page header (one card with period and refresh).
  const summary = <TeamShiftSummary level={level} onLevel={setLevel} />;

  if (selected)
    return (
      <>
        {summary}
        <MyLeadsPage key={selected.value} opsUuid={selected.value} picker={select} />
      </>
    );

  return (
    <>
      {summary}
      <PickerBar>
        {select}
        {rosterError ? (
          <span
            role="alert"
            className="inline-flex flex-wrap items-center gap-2 text-xs text-state-error-text"
          >
            {rosterError}
            <Btn size="row" onClick={loadRoster}>
              Try again
            </Btn>
          </span>
        ) : null}
        {unknownRep ? (
          <span role="status" className="text-xs text-state-warning-text">
            That rep is not an active L1, L2 or L3 member. Pick someone else.
          </span>
        ) : null}
        {options && options.length === 0 ? (
          <span className="text-xs text-content-muted">
            No active L1, L2 or L3 reps on the roster.
          </span>
        ) : null}
        {!rosterError && !unknownRep && options?.length !== 0 ? (
          <span data-testid="team-leads-empty" className="text-xs text-content-muted">
            Pick a team member to see their leads page exactly as they see it.
          </span>
        ) : null}
      </PickerBar>
    </>
  );
}

export default TeamLeadsPage;
